import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, HostListener, ViewChild } from '@angular/core';
import { FileCaptureService } from 'app/service/file-capture.service';
import { FileServiceService } from 'app/service/file-service.service';
import { environment } from 'environments/environment';

@Component({
  standalone: false,
  selector: 'app-file-status',
  templateUrl: './file-status.component.html',
  styleUrls: ['./file-status.component.css']
})
export class FileStatusComponent implements OnInit, OnDestroy  {
  
  private readonly UPLOAD_CHUNK_SIZE_BYTES = 100 * 1024 * 1024;
  private reviewAndScanRequestedAttachmentKeys = new Set<any>();
  private pendingTransferResolvers = new Map<string, () => void>();

  FilesToUpload: any = [];
  BufferChunkSize: any;
  fileUploadMap: any = {};
  totalUploadedFiles: any = 0;
  canUpload: boolean = true;
  hasUpdate: boolean;
  statusPollingTimer: any = null;
  isPollRequestInFlight: boolean = false;
  uploadCompletionTimer: any = null;
  hasUploadErrors: boolean = false;

  showReUploadModal: boolean = false;
  reUploadFilesList: any[] = [];
  reUploadError: string = '';
  reUploadNewFiles: File[] = [];

  @ViewChild('reUploadFileInput') reUploadFileInput: ElementRef | undefined;

  @Input() files: any[] = [];
  @Input() uploadedFiles: File[] | null = null;
  @Output() uploadCompleted = new EventEmitter<any>();
  @Output() uploadClosed = new EventEmitter<void>();

  constructor(
    public service: FileServiceService,
    private fcservice: FileCaptureService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
) { }

  ngOnInit(): void {
    this.startUpload();
  }

  ngOnDestroy(): void {
    this.stopStatusPolling();

    if (this.uploadCompletionTimer) {
      clearInterval(this.uploadCompletionTimer);
      this.uploadCompletionTimer = null;
    }
  }

  private logUploadState(stage: string, extra: any = {}) {
    console.log(`[FileStatus][${stage}]`, {
      referenceId: this.service.getCache('referenceId'),
      cachedCaseActivityKey: this.service.getCache('caseActivityKey'),
      attachmentSaved: this.service.getCache('attachmentSaved'),
      attachmentSavedForRef: this.service.getCache('attachmentSavedForRef'),
      sessionId: this.fcservice.getActiveReferenceSessionId(),
      filesInModal: this.files?.map((file: any) => ({
        fileName: file.fileName,
        isUpload: file.isUpload,
        attachmentKey: file.attachmentKey,
        uploadFailed: file.uploadFailed
      })),
      ...extra
    });
  }

  private isInvalidAttachmentKey(data: any): boolean {
    return data?.attachmentKey === -1 || data?.attachmentKey === 0;
  }

  private requestReviewAndScanForAttachmentKeys(attachmentKeyList: any[]): Promise<void> {
    const validAttachmentKeys = attachmentKeyList.filter((attachmentKey: any) =>
      !!attachmentKey && !this.reviewAndScanRequestedAttachmentKeys.has(attachmentKey)
    );

    if (!validAttachmentKeys.length) {
      return Promise.resolve();
    }

    validAttachmentKeys.forEach((attachmentKey: any) => this.reviewAndScanRequestedAttachmentKeys.add(attachmentKey));

    this.logUploadState('requestReviewAndScanForAttachmentKeys', {
      attachmentKeyList: validAttachmentKeys
    });

    return new Promise((resolve, reject) => {
      this.fcservice.ReviewAndScanAttachmnents({
        referenceId: this.service.getCache('referenceId'),
        attachmentKeyList: validAttachmentKeys
      }).subscribe({
        next: () => resolve(),
        error: (err) => {
          validAttachmentKeys.forEach((attachmentKey: any) => this.reviewAndScanRequestedAttachmentKeys.delete(attachmentKey));
          this.logUploadState('requestReviewAndScanForAttachmentKeys-error', {
            attachmentKeyList: validAttachmentKeys,
            error: err
          });
          reject(err);
        }
      });
    });
  }

  private waitForFileScanResult(fileName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const timer = setInterval(() => {
        const file = this.files.find((item: any) => item.fileName === fileName);

        if (!file) {
          clearInterval(timer);
          reject(new Error('File not found while waiting for scan result'));
          return;
        }

        if (file.scanStatus === 1) {
          clearInterval(timer);
          this.logUploadState('waitForFileScanResult-success', {
            fileName,
            attachmentKey: file.attachmentKey,
            scanStatus: file.scanStatus
          });
          resolve();
          return;
        }

        if (file.scanStatus === 99 || file.uploadFailed === true || Number(file.percentage) === 500 || Number(file.percentage) === 600) {
          clearInterval(timer);
          this.logUploadState('waitForFileScanResult-failed', {
            fileName,
            attachmentKey: file.attachmentKey,
            scanStatus: file.scanStatus,
            uploadFailed: file.uploadFailed,
            percentage: file.percentage
          });
          reject(new Error('First file scan failed'));
          return;
        }

        if ((Date.now() - startedAt) > 180000) {
          clearInterval(timer);
          this.logUploadState('waitForFileScanResult-timeout', {
            fileName,
            attachmentKey: file.attachmentKey,
            scanStatus: file.scanStatus,
            uploadStatus: file.uploadStatus,
            percentage: file.percentage
          });
          reject(new Error('Timed out waiting for first file scan result'));
        }
      }, 500);
    });
  }
  
  async uploadFirstFile(firstFile: any, deferUpload: boolean = false) {
    const attachment = {
      fileName: firstFile.fileName.substring(firstFile.fileName.lastIndexOf('\\') + 1),
      fileType: firstFile.fileName.substring(firstFile.fileName.lastIndexOf('.') + 1),
      percentage: 0,
      fileSize: firstFile.fileSize || 0,
      sizeReadable: firstFile.sizeReadable || '',
      isFirst: true,
      fileId: 0,
      activityName: 'Deconfliction',
      referenceId: this.service.getCache('referenceId'),
      targetFile: this.uploadedFiles?.find(x => x.name === firstFile.fileName.substring(firstFile.fileName.lastIndexOf('\\') + 1)),
      attachmentLength: firstFile.fileSize,
      noOfchunksSent:0,
      uploadStatus: 'uploading',
      statusText: 'Upload in progress',
      failureToUpload: false
    };

    this.logUploadState('uploadFirstFile-request', {
      fileName: attachment.fileName,
      isFirst: attachment.isFirst,
      caseActivityKey: this.service.getCache('caseActivityKey'),
      attachmentLength: attachment.attachmentLength
    });

    this.FilesToUpload.push(attachment);
    return new Promise((resolve, reject) => {
      this.fcservice.addAttachmentToDB(attachment).subscribe({
        next: (data: any) => {
          this.logUploadState('uploadFirstFile-response', {
            fileName: attachment.fileName,
            response: data
          });

          if (this.isInvalidAttachmentKey(data)) {
            this.logUploadState('uploadFirstFile-invalidAttachmentKey', {
              fileName: attachment.fileName,
              response: data
            });
            const failedFileName = attachment.fileName;
            const failedUploadFile = this.FilesToUpload.find((x: any) => x.fileName === failedFileName);
            if (failedUploadFile) {
              failedUploadFile.uploadStatus = 'failed';
              failedUploadFile.statusText = 'Error in upload';
              failedUploadFile.failureToUpload = true;
              failedUploadFile.uploadFailed = true;
              failedUploadFile.percentage = 600;
            }
            const index = this.files.findIndex((x: any) => x.fileName.substring(x.fileName.lastIndexOf('\\') + 1) === failedFileName);         
            if (index > -1) {
              this.files[index].attachmentKey = data.attachmentKey;
              this.files[index].isFirst = false;
              this.files[index].isUpload = 0;
              this.files[index].isVisible = data.attachmentKey + "_99";
              this.files[index].uploadFailed = true;
              this.files[index].percentage = 600;
              this.files[index].uploadStatus = 'failed';
              this.files[index].statusText = 'Error in upload';
              this.service.setCacheForitems([{ id: "files", value: this.files }]);
            }
            this.cdr.detectChanges();
            reject('Invalid attachment key');
          } else {
            if (data.caseActivityKey) {
              this.service.referenceFileDetails.caseActivityKey = data.caseActivityKey;
              this.service.setCacheForitems([{ id: "caseActivityKey", value: data.caseActivityKey }]);
            }
            this.service.setCacheForitems([
              { id: "attachmentSaved", value: "Saved" },
              { id: "attachmentSavedForRef", value: this.service.getCache('referenceId') }
            ]);

            this.FilesToUpload[0].attachmentKey = data.attachmentKey;
            if (attachment.isFirst && data.caseActivityKey) {
              this.service.referenceFileDetails.caseActivityKey = data.caseActivityKey;
              this.service.setCacheForitems([{ id: "caseActivityKey", value: data.caseActivityKey }]);
            }
            const index = this.files.findIndex((x: any) =>x.fileName.substring(x.fileName.lastIndexOf('\\') + 1) === this.FilesToUpload[0].fileName);            if (index > -1) {
              this.files[index].attachmentKey = data.attachmentKey;
              this.files[index].isFirst = false;
              this.files[index].isUpload = 0;
              this.files[index].isVisible = data.attachmentKey + "_0";
              this.service.setCacheForitems([{ id: "files", value: this.files }]);
            }
            if (deferUpload) {
              resolve(data);
              return;
            }

            this.processFiles(attachment.fileId);
            this.waitForTransferCompletion(attachment.fileName).then(() => resolve(data));
          }
        },
        error: (err) => {
          this.logUploadState('uploadFirstFile-error', {
            fileName: attachment.fileName,
            error: err
          });
          console.error('Error uploading first file:', err);
          reject(err);
        }
      });
    });
  }

  async uploadFiles(files: any, newSessionCaseActivityKey?: string, deferUpload: boolean = false) {
    this.logUploadState('uploadFiles-batch-start', {
      fileNames: files.map((file: any) => file.fileName),
      newSessionCaseActivityKey
    });

    // Step 1: Register all files in the UI immediately so user sees them all
    const attachments: any[] = [];
    for (let i = 0; i < files.length; i++) {
      const attachment: any = {
        fileName: files[i].fileName.substring(files[i].fileName.lastIndexOf('\\') + 1),
        fileType: files[i].fileName.substring(files[i].fileName.lastIndexOf('.') + 1),
        percentage: 0,
        fileSize: files[i].fileSize || 0,
        sizeReadable: files[i].sizeReadable || '',
        isFirst: false,
        fileId: i + 1,
        activityName: 'Deconfliction',
        referenceId: this.service.getCache('referenceId'),
        targetFile: this.uploadedFiles?.find( x => x.name === files[i].fileName.substring(files[i].fileName.lastIndexOf('\\') + 1)),
        attachmentLength: files[i].fileSize,
        noOfchunksSent:0,
        uploadStatus: 'uploading',
        statusText: 'Upload in progress',
        failureToUpload: false
      };

      if (newSessionCaseActivityKey) {
        attachment.caseActivityKey = newSessionCaseActivityKey;
      }

      this.FilesToUpload.push(attachment);
      attachments.push(attachment);

      this.logUploadState('uploadFiles-attachment-created', {
        fileName: attachment.fileName,
        fileId: attachment.fileId,
        caseActivityKey: attachment.caseActivityKey,
        isFirst: attachment.isFirst
      });
    }
    this.cdr.detectChanges();

    // Step 2: Process files one by one sequentially
    for (const attachment of attachments) {
      await this.addAttachmentToDbAsync(attachment, attachment.targetFile, attachment.fileId, deferUpload);
    }

    return attachments;
  }

  async startUpload() {
    this.reviewAndScanRequestedAttachmentKeys.clear();
    const newFiles = this.files.filter((x: any) => x.isUpload == 1);

    this.logUploadState('startUpload-init', {
      uploadedFileNames: this.uploadedFiles?.map((file: File) => file.name) || [],
      newFileNames: newFiles.map((file: any) => file.fileName)
    });

    if ((!this.uploadedFiles || this.uploadedFiles.length === 0) &&
        this.files.some((f: any) => f.attachmentKey)) {
      this.restoreAfterReload();
      return;
    }

    if (newFiles.length === 0) {
      if (!this.hasErroredFiles) {
        this.uploadCompleted.emit({
          files: this.files,
          hasInvalidFiles: false,
          hasUploadErrors: false
        });
      }

      return;
    }

    newFiles.forEach((file: any) => {
      file.uploadFailed = false;
      file.scanStatus = 0;
      file.uploadStatus = 'uploading';
      file.statusText = 'Upload in progress';
    });

    this.startStatusPolling();


    try {
      const attachmentSaved = this.service.getCache('attachmentSaved');
      const cachedCaseActivityKey = this.service.getCache('caseActivityKey');
      const currentReferenceId = this.service.getCache('referenceId');
      const attachmentSavedForRef = this.service.getCache('attachmentSavedForRef');
      const uploadStateIsValid = attachmentSaved && cachedCaseActivityKey && attachmentSavedForRef === currentReferenceId;

      this.logUploadState('startUpload-branch-check', {
        uploadStateIsValid,
        currentReferenceId,
        cachedCaseActivityKey,
        attachmentSavedForRef
      });

      if (!uploadStateIsValid) {
        this.logUploadState('startUpload-fresh-session-path', {
          firstFileName: newFiles[0]?.fileName
        });
        const firstFileResult: any = await this.uploadFirstFile(newFiles[0], true);
        const firstFileName = newFiles[0].fileName.substring(newFiles[0].fileName.lastIndexOf('\\') + 1);
        let remainingAttachments: any[] = [];

        const remainingFiles = newFiles.slice(1);
        if (remainingFiles.length > 0) {
          const newCaseActivityKey = this.service.getCache('caseActivityKey');
          this.logUploadState('startUpload-remaining-files', {
            remainingFileNames: remainingFiles.map((file: any) => file.fileName),
            newCaseActivityKey,
            phase: 'metadata-only'
          });
          remainingAttachments = await this.uploadFiles(remainingFiles, newCaseActivityKey, true);
        }

        this.processFiles(0);
        await this.waitForTransferCompletion(firstFileName);

        if (firstFileResult?.attachmentKey) {
          this.logUploadState('startUpload-first-file-review-and-scan', {
            fileName: firstFileName,
            attachmentKey: firstFileResult.attachmentKey
          });
          try {
            await this.requestReviewAndScanForAttachmentKeys([firstFileResult.attachmentKey]);
          } catch (scanErr) {
            this.logUploadState('startUpload-first-file-review-and-scan-request-failed-continue-remaining', {
              fileName: firstFileName,
              attachmentKey: firstFileResult.attachmentKey,
              error: scanErr
            });
          }
        }

        if (remainingAttachments.length > 0) {
          await this.uploadPreparedAttachments(remainingAttachments);
        }
      } else {
        this.logUploadState('startUpload-reuse-session-path', {
          reusedCaseActivityKey: cachedCaseActivityKey,
          newFileNames: newFiles.map((file: any) => file.fileName)
        });

        const preparedAttachments = await this.uploadFiles(newFiles, cachedCaseActivityKey, true);
        await this.uploadPreparedAttachments(preparedAttachments);
      }

      this.waitForUploadCompletion();
    } catch (err) {
      console.error('Upload start failed', err);
      this.waitForUploadCompletion();
    }
  }

  private addAttachmentToDbAsync(attachment, targetFile, i, deferUpload: boolean = false): Promise<void> {
    var getFileIndex = this.FilesToUpload.findIndex(x => x.fileId === i);
    this.FilesToUpload[getFileIndex].attachmentLength = attachment.fileSize;
    this.FilesToUpload[getFileIndex].targetFile = targetFile;
    this.FilesToUpload[getFileIndex].referenceId = this.service.getCache('referenceId');

    this.logUploadState('addAttachmentToDbAsync-request', {
      fileName: attachment.fileName,
      fileId: i,
      caseActivityKey: attachment.caseActivityKey,
      attachmentLength: attachment.fileSize
    });

    return new Promise<void>((resolve) => {
      const markAttachmentInvalid = (responseData: any) => {
        const index = this.files.findIndex(x => x.fileName === this.FilesToUpload[getFileIndex].fileName);
        if (index > -1) {
          this.files[index].attachmentKey = responseData.attachmentKey;
          this.files[index].isFirst = false;
          this.files[index].isUpload = 0;
          this.files[index].isVisible = responseData.attachmentKey + "_99";
          this.files[index].uploadFailed = true;
          this.files[index].percentage = 600;
          this.files[index].uploadStatus = 'failed';
          this.files[index].statusText = 'Error in upload';
          this.service.setCacheForitems([{ id: "files", value: this.files }]);
        }

        const idx = this.FilesToUpload.findIndex(x => x.fileId === i);
        if (idx > -1) {
          this.FilesToUpload[idx].uploadStatus = 'failed';
          this.FilesToUpload[idx].statusText = 'Error in upload';
          this.FilesToUpload[idx].failureToUpload = true;
          this.FilesToUpload[idx].uploadFailed = true;
          this.FilesToUpload[idx].percentage = 600;
        }

        this.cdr.detectChanges();
        resolve();
      };

      const handleAttachmentSuccess = (responseData: any) => {
        const idx = this.FilesToUpload.findIndex(x => x.fileId === i);
        if (idx > -1) {
          this.FilesToUpload[idx].attachmentKey = responseData.attachmentKey;
          const cachedCaseActivityKey = this.service.getCache('caseActivityKey') || this.service.referenceFileDetails.caseActivityKey;
          const effectiveCaseActivityKey = attachment.caseActivityKey || cachedCaseActivityKey || responseData.caseActivityKey;
          const serverCaseActivityKey = responseData.caseActivityKey || effectiveCaseActivityKey;

          if (effectiveCaseActivityKey) {
            this.FilesToUpload[idx].caseActivityKey = effectiveCaseActivityKey;
          }

          if (serverCaseActivityKey) {
            this.FilesToUpload[idx].serverCaseActivityKey = serverCaseActivityKey;
          }

          if (responseData.caseActivityKey && !cachedCaseActivityKey) {
            this.service.referenceFileDetails.caseActivityKey = responseData.caseActivityKey;
            this.service.setCacheForitems([{ id: "caseActivityKey", value: responseData.caseActivityKey }]);
          }

          if (responseData.caseActivityKey && effectiveCaseActivityKey && responseData.caseActivityKey !== effectiveCaseActivityKey) {
            this.logUploadState('addAttachmentToDbAsync-preserveCaseActivityKey', {
              fileName: attachment.fileName,
              fileId: i,
              responseCaseActivityKey: responseData.caseActivityKey,
              effectiveCaseActivityKey
            });
          }
          const index = this.files.findIndex(x => x.fileName === this.FilesToUpload[idx].fileName);
          if (index > -1) {
            this.files[index].attachmentKey = responseData.attachmentKey;
            this.files[index].caseActivityKey = this.FilesToUpload[idx].caseActivityKey;
            this.files[index].serverCaseActivityKey = this.FilesToUpload[idx].serverCaseActivityKey;
            this.files[index].isFirst = false;
            this.files[index].isUpload = 0;
            this.files[index].isVisible = responseData.attachmentKey + "_0";
            this.service.setCacheForitems([{ id: "files", value: this.files }]);
          }
          if (deferUpload) {
            resolve();
            return;
          }

          this.processFiles(i);
        }

        this.waitForTransferCompletion(attachment.fileName).then(() => resolve());
      };

      this.fcservice.addAttachmentToDB(attachment).subscribe({
        next: (data: any) => {
          this.logUploadState('addAttachmentToDbAsync-response', {
            fileName: attachment.fileName,
            fileId: i,
            response: data
          });

          if (this.isInvalidAttachmentKey(data)) {
            this.logUploadState('addAttachmentToDbAsync-invalidAttachmentKey', {
              fileName: attachment.fileName,
              fileId: i,
              response: data
            });
            if (attachment.caseActivityKey) {
              const retryAttachment = { ...attachment };
              delete retryAttachment.caseActivityKey;

              this.logUploadState('addAttachmentToDbAsync-retryWithoutCaseActivity', {
                fileName: attachment.fileName,
                fileId: i,
                originalCaseActivityKey: attachment.caseActivityKey
              });

              this.fcservice.addAttachmentToDB(retryAttachment).subscribe({
                next: (retryData: any) => {
                  this.logUploadState('addAttachmentToDbAsync-retryResponse', {
                    fileName: attachment.fileName,
                    fileId: i,
                    response: retryData
                  });

                  if (this.isInvalidAttachmentKey(retryData)) {
                    this.logUploadState('addAttachmentToDbAsync-retryInvalidAttachmentKey', {
                      fileName: attachment.fileName,
                      fileId: i,
                      response: retryData
                    });

                    const retryAsFirstAttachment = { ...retryAttachment, isFirst: true };
                    this.logUploadState('addAttachmentToDbAsync-retryAsFirst', {
                      fileName: attachment.fileName,
                      fileId: i
                    });

                    this.fcservice.addAttachmentToDB(retryAsFirstAttachment).subscribe({
                      next: (retryAsFirstData: any) => {
                        this.logUploadState('addAttachmentToDbAsync-retryAsFirstResponse', {
                          fileName: attachment.fileName,
                          fileId: i,
                          response: retryAsFirstData
                        });

                        if (this.isInvalidAttachmentKey(retryAsFirstData)) {
                          this.logUploadState('addAttachmentToDbAsync-retryAsFirstInvalidAttachmentKey', {
                            fileName: attachment.fileName,
                            fileId: i,
                            response: retryAsFirstData
                          });
                          markAttachmentInvalid(retryAsFirstData);
                          return;
                        }

                        handleAttachmentSuccess(retryAsFirstData);
                      },
                      error: (retryAsFirstErr) => {
                        this.logUploadState('addAttachmentToDbAsync-retryAsFirstError', {
                          fileName: attachment.fileName,
                          fileId: i,
                          error: retryAsFirstErr
                        });
                        console.error('Retry addAttachmentToDB as first file failed:', retryAsFirstErr);
                        this.markFileUploadError(attachment.fileName);
                        resolve();
                      }
                    });
                    return;
                  }

                  handleAttachmentSuccess(retryData);
                },
                error: (retryErr) => {
                  this.logUploadState('addAttachmentToDbAsync-retryError', {
                    fileName: attachment.fileName,
                    fileId: i,
                    error: retryErr
                  });
                  console.error('Retry addAttachmentToDB failed:', retryErr);
                  this.markFileUploadError(attachment.fileName);
                  resolve();
                }
              });
              return;
            }

            markAttachmentInvalid(data);
          } else {
            handleAttachmentSuccess(data);
          }
        },
        error: (err) => {
          this.logUploadState('addAttachmentToDbAsync-error', {
            fileName: attachment.fileName,
            fileId: i,
            error: err
          });
          console.error('Error adding attachment to DB:', err);
          this.markFileUploadError(attachment.fileName);
          resolve();
        }
      });
    });
  }

  private async uploadPreparedAttachments(attachments: any[]): Promise<void> {
    for (const attachment of attachments) {
      this.processFiles(attachment.fileId);
      await this.waitForTransferCompletion(attachment.fileName);
    }
  }

  processFiles(i) {
    const getFileIndex = this.FilesToUpload.findIndex(x => x.fileId === i);
    if (getFileIndex === -1) {
      return;
    }

    if (this.FilesToUpload[getFileIndex].attachmentKey && Math.abs(parseInt(this.FilesToUpload[getFileIndex].fileSize)) >= this.UPLOAD_CHUNK_SIZE_BYTES) {
      this.createandUploadChunks(this.FilesToUpload[getFileIndex], this.FilesToUpload[getFileIndex].targetFile, i);
      this.nextBatchProcess(i);
    }
    else {
      if (!this.FilesToUpload[getFileIndex].targetFile) {
        this.markFileUploadError(this.FilesToUpload[getFileIndex].fileName);
        return;
      }

      if (!this.FilesToUpload[getFileIndex].attachmentKey) {
        this.markFileUploadError(this.FilesToUpload[getFileIndex].fileName);
        return;
      }

      this.upload(this.FilesToUpload[getFileIndex].targetFile, i, null, this.FilesToUpload[getFileIndex].fileSize, this.FilesToUpload[getFileIndex].fileName,
        0, this.FilesToUpload[getFileIndex].attachmentKey, i);
    }
  }

  createandUploadChunks(attachment, targetFile, i) {
    var fileChunks = [];
    var Size = attachment.fileSize;
    var num = this.UPLOAD_CHUNK_SIZE_BYTES;
    if (Size / num > 256) {
      this.BufferChunkSize = Size / 256;
    }
    else
      this.BufferChunkSize = this.UPLOAD_CHUNK_SIZE_BYTES;
    var ReadBuffer_Size = 1024;
    var FileStreamPos = 0;
    var EndPos = this.BufferChunkSize;

    var fileChunkId = 0;
    while (FileStreamPos < Size) {
      if (!targetFile) {
        this.markFileUploadError(attachment.fileName);
        return;
      }
      fileChunks.push({ targetFile: targetFile.slice(FileStreamPos, EndPos), id: fileChunkId });
      FileStreamPos = EndPos;
      EndPos = FileStreamPos + this.BufferChunkSize;
      fileChunkId++;
    }
    var getFileIndex = this.FilesToUpload.findIndex(x => x.fileId === i);

    this.FilesToUpload[getFileIndex].fileChunksLength = fileChunks.length;
    this.FilesToUpload[getFileIndex].fileChunks = fileChunks;
    this.FilesToUpload[getFileIndex].lastFileChunkId = fileChunkId;
  }

  nextBatchProcess(i) {
    var getFileIndex = this.FilesToUpload.findIndex(x => x.fileId === i);
    var remainder = this.FilesToUpload[getFileIndex].fileChunksLength % 5;
    var num = 5;
    var total = this.FilesToUpload[getFileIndex].fileChunks.length;
    if (total === remainder || total < 5) {
      num = this.FilesToUpload[getFileIndex].fileChunks.length;
    }

    for (let countChunk = 0; countChunk < total; countChunk++) {
      if (this.FilesToUpload[getFileIndex].fileChunks[countChunk] != null) {
        this.upload(this.FilesToUpload[getFileIndex].fileChunks[countChunk].targetFile,
          this.FilesToUpload[getFileIndex].fileChunks[countChunk].id,
          null,
          this.FilesToUpload[getFileIndex].fileSize,
          this.FilesToUpload[getFileIndex].fileName,
          this.FilesToUpload[getFileIndex].fileChunksLength,
          this.FilesToUpload[getFileIndex].attachmentKey,
          i
        );
      }
    }
  }

  upload(chunk, chunkorfileId, objXhr1, fileSize, fileName, totalChunks, attachmentKey, itemindex) {
    try {
      if (fileSize <= this.UPLOAD_CHUNK_SIZE_BYTES) {
        chunkorfileId = this.FilesToUpload.findIndex(x => x.fileId === chunkorfileId);
      }
      var baseUrl = environment.baseUri;
      const uploadFile = this.FilesToUpload.find((x: any) => x.fileId === itemindex) || this.FilesToUpload.find((x: any) => x.attachmentKey === attachmentKey);
      const caseActivityKeyForUpload = uploadFile?.caseActivityKey || this.service.getCache('caseActivityKey') || this.service.referenceFileDetails.caseActivityKey;
      if (caseActivityKeyForUpload) {
        this.service.referenceFileDetails.caseActivityKey = caseActivityKeyForUpload;
      }
      const sessionId = window.sessionStorage.getItem(this.fcservice.SESSION_KEY) || '';
      var url =
        baseUrl + "FileCapture/UploadAttachment?attachmentKey=" + attachmentKey + "&referenceId=" + this.service.referenceFileDetails.referenceId + "&caseactivitykey=" + caseActivityKeyForUpload + "&sessionId=" + encodeURIComponent(sessionId);

      var objXhr;
      objXhr = new XMLHttpRequest();
      var data = new FormData();

      data.append('uploadedFile', chunk);
      objXhr.upload.fileId = chunkorfileId;
      objXhr.upload.fileName = fileName;

      objXhr.timeout = 3600000;
      objXhr.open('POST', url);

      objXhr.onerror = () => {
        this.zone.run(() => {
          this.markFileUploadError(fileName);
        });
      };

      objXhr.ontimeout = () => {
        this.zone.run(() => {
          this.markFileUploadError(fileName);
        });
      };

      objXhr.setRequestHeader("X-Content-Name", fileName);
      objXhr.setRequestHeader("Ocp-Apim-Subscription-Key", environment.subscriptionKey);
      if (totalChunks !== 0) {

        objXhr.setRequestHeader("X-Chunk-Id", chunkorfileId);
        objXhr.setRequestHeader("X-Content-Length", fileSize);
        objXhr.setRequestHeader("X-Total-Chunks", totalChunks);

        if (chunkorfileId === totalChunks - 1) {
          objXhr.setRequestHeader("X-Last-Chunk", "true");
        }
        else
          objXhr.setRequestHeader("X-Last-Chunk", "false");
      }

      if (fileSize <= this.UPLOAD_CHUNK_SIZE_BYTES) {
        this.FilesToUpload[chunkorfileId].xmlObj = objXhr;
        objXhr.upload.addEventListener("progress", (evt) => this.updateProgressForSmallFiles(evt), false);
      }
      else
        objXhr.upload.addEventListener("progress", (evt) => this.updateProgress(evt), false);

      objXhr.onload = () => {
        this.zone.run(() => {
          if (objXhr.status < 200 || objXhr.status >= 300) {
            this.markFileUploadError(fileName);
            return;
          }

          if (fileSize > this.UPLOAD_CHUNK_SIZE_BYTES) {
            if (this.fileUploadMap[attachmentKey] === undefined) {
              this.fileUploadMap[attachmentKey] = 1;
            } else {
              this.fileUploadMap[attachmentKey]++;
            }

            if (totalChunks === this.fileUploadMap[attachmentKey]) {
              this.totalUploadedFiles++;

              this.fcservice.commitFile(attachmentKey, fileName, totalChunks).subscribe({
                next: () => {
                  this.updateFileSuccess(fileName);
                },
                error: () => {
                  this.markFileUploadError(fileName);
                }
              });
            }
          } else {
            this.totalUploadedFiles++;
            this.updateFileSuccess(fileName);
          }
        });
      };

      objXhr.send(data);
    }
    catch (e) {
      const result = (e as Error).message as string;

      if (result.includes('String contains non ISO-8859-1 code point')) {
        this.markFileInvalid(fileName);
      } else {
        this.markFileUploadError(fileName);
      }
    }
  }

  updateProgressForSmallFiles(e: any) {
    const filename = e.currentTarget.fileName;
    let keyOfFilesmall: any;

    this.FilesToUpload.forEach((value: any, key: any) => {
      if (value.fileName === filename) {
        keyOfFilesmall = key;
      }
    });

    this.zone.run(() => {
      const percent = 100;
      this.FilesToUpload[keyOfFilesmall].percentage = percent;
      this.FilesToUpload[keyOfFilesmall].isReadyToDownload = true;

      const index = this.files.findIndex((x: any) => x.fileName === this.FilesToUpload[keyOfFilesmall].fileName);
      if (index > -1) {
        this.files[index].percentage = percent;
        this.files[index].isReadyToDownload = true;
      }

      this.service.setCacheForitems([{ id: 'files', value: this.files }]);
      this.hasUpdate = true;
      this.cdr.detectChanges();
    });
  }

  updateProgress(e) {
    var chunkid = e.currentTarget.fileId;
    var filename = e.currentTarget.fileName;

    let keyOfFile;

    this.FilesToUpload.forEach((value, key) => {
      if (value.fileName === filename) {
        keyOfFile = key;
      }
    });

    this.zone.run(() => {
      var perc = ((this.FilesToUpload[keyOfFile].noOfchunksSent * this.BufferChunkSize) / this.FilesToUpload[keyOfFile].fileSize) * 100;
      if (perc < 100)
        this.FilesToUpload[keyOfFile].percentage = Math.round(perc);
      else if (perc >= 100 && this.FilesToUpload[keyOfFile].fileChunksLength === this.FilesToUpload[keyOfFile].noOfchunksSent) {
        this.FilesToUpload[keyOfFile].isReadyToDownload = true;
        this.FilesToUpload[keyOfFile].percentage = 100;
        let index = this.files.findIndex(x => x.fileName === this.FilesToUpload[keyOfFile].fileName);
        if (index > -1) {
          this.files[index].percentage = 100;
          this.files[index].isReadyToDownload = true; 
        }   
        this.service.setCacheForitems([{ id: "files", value: this.files }]);
        this.hasUpdate = true;
      }
    });
    
    var idx = this.FilesToUpload[keyOfFile].fileChunks.findIndex(function (x) {
      return x.id === chunkid;
    });

    if (idx > -1) {
      this.FilesToUpload[keyOfFile].fileChunks.splice(idx, 1);
      this.FilesToUpload[keyOfFile].noOfchunksSent++;
    }

    if (this.FilesToUpload[keyOfFile].fileChunksLength === this.FilesToUpload[keyOfFile].noOfchunksSent) {
      if (this.FilesToUpload[keyOfFile].isReadyToDownload === true) {
        this.FilesToUpload[keyOfFile].percentage = 100;
        this.FilesToUpload[keyOfFile].isReadyToDownload = true;
        let index = this.files.findIndex(x => x.fileName === this.FilesToUpload[keyOfFile].fileName);
        if (index > -1) {
          this.files[index].percentage = 100;
          this.files[index].isReadyToDownload = true; 
        }  
        this.service.setCacheForitems([{ id: "files", value: this.files }]);
        const attachment = {
        referenceId: '',
        attachmentKeyList: []
      };
      attachment.referenceId = this.service.getCache('referenceId');
      attachment.attachmentKeyList = this.files.map(x => {
        if(x.attachmentKey != null && typeof(x.attachmentKey) !== 'undefined' && x.isVisible != x.attachmentKey + '_99'){
          return x.attachmentKey;
        }
      });      
      }
    }
  }

  startStatusPolling() {
    if (this.statusPollingTimer) {
      clearInterval(this.statusPollingTimer);
    }

    this.statusPollingTimer = setInterval(() => {
      if (this.isPollRequestInFlight) {
        return;
      }

      const filesStillPending = this.files.filter((x: any) => {
        if (!x.attachmentKey) {
          return false;
        }

        const isSuccess = x.scanStatus === 1 || x.isVisible === x.attachmentKey + '_1';
        const isInvalid = x.scanStatus === 99 || x.isVisible === x.attachmentKey + '_99';
        const isUploadFailed = x.uploadFailed === true || x.percentage === 600;
        const reviewRequested = this.reviewAndScanRequestedAttachmentKeys.has(x.attachmentKey);

        return reviewRequested && !isSuccess && !isInvalid && !isUploadFailed;
      });

      const attachment = {
        referenceId: this.service.getCache('referenceId'),
        attachmentKeyList: filesStillPending.map((x: any) => x.attachmentKey)
      };

      if (!attachment.attachmentKeyList.length) {
        const filesStillUploading = this.files.some((x: any) =>
          !x.attachmentKey && x.uploadStatus === 'uploading'
        );

        const filesAwaitingReviewRequest = this.files.some((x: any) =>
          x.attachmentKey &&
          x.uploadStatus === 'complete' &&
          x.uploadFailed !== true &&
          x.scanStatus !== 1 &&
          x.scanStatus !== 99 &&
          Number(x.percentage) !== 500 &&
          Number(x.percentage) !== 600 &&
          !this.reviewAndScanRequestedAttachmentKeys.has(x.attachmentKey)
        );

        if (!filesStillUploading && !filesAwaitingReviewRequest) {
          this.stopStatusPolling();
        }
        return;
      }

      this.isPollRequestInFlight = true;

      this.fcservice.GetAttachmentStatus(attachment).subscribe({
        next: (status: any) => {
          if (status?.length) {
            this.files.forEach((item: any) => {
              const matched = status.find((y: any) => y.id == item.attachmentKey);
              if (!matched) {
                return;
              }

              const scanStatus = matched.VirusScanStatus ?? 0;
              item.scanStatus = scanStatus;
              item.isVisible = item.attachmentKey + '_' + scanStatus;

              if (scanStatus === 1) {
                item.percentage = 100;
                item.uploadFailed = false;
                item.isReadyToDownload = true;
                item.uploadStatus = 'complete';
                item.statusText = 'Upload complete';
                item.isuploaded = true;
              } else if (scanStatus === 99) {
                item.percentage = 500;
                item.uploadFailed = false;
                item.uploadStatus = 'failed';
                item.statusText = 'Error in upload';
              }

              const uploadFileMatch = this.FilesToUpload.find((f: any) => f.fileName === item.fileName);
              if (uploadFileMatch) {
                uploadFileMatch.scanStatus = scanStatus;
                if (scanStatus === 1) {
                  uploadFileMatch.percentage = 100;
                  uploadFileMatch.uploadFailed = false;
                  uploadFileMatch.uploadStatus = 'complete';
                  uploadFileMatch.statusText = 'Upload complete';
                } else if (scanStatus === 99) {
                  uploadFileMatch.percentage = 500;
                  uploadFileMatch.uploadFailed = false;
                  uploadFileMatch.uploadStatus = 'failed';
                  uploadFileMatch.statusText = 'Error in upload';
                }
              }
            });

            this.service.setCacheForitems([{ id: 'files', value: this.files }]);
            this.cdr.detectChanges();
          }

          const allFinal = this.files
            .filter((x: any) => x.attachmentKey)
            .every((item: any) => {
              return (
                item.scanStatus === 1 ||
                item.scanStatus === 99 ||
                item.uploadFailed === true ||
                item.percentage === 500 ||
                item.percentage === 600
              );
            });

          if (allFinal) {
            this.stopStatusPolling();
          }
        },
        error: (err) => {
          console.error('pollStatus failed', err);
        },
        complete: () => {
          this.isPollRequestInFlight = false;
        }
      });
    }, 3000);
  }

  stopStatusPolling() {
    if (this.statusPollingTimer) {
      clearInterval(this.statusPollingTimer);
      this.statusPollingTimer = null;
    }
  }

  waitForUploadCompletion() {
    if (this.uploadCompletionTimer) {
      clearInterval(this.uploadCompletionTimer);
    }

    this.uploadCompletionTimer = setInterval(() => {
      const filesStillAwaitingUpload = this.files.some((item: any) =>
        !item.attachmentKey && item.uploadStatus === 'uploading' && item.uploadFailed !== true
      );
      if (filesStillAwaitingUpload) {
        return;
      }

      const activeUploads = this.files.filter((item: any) =>
        item.attachmentKey || item.uploadFailed === true || item.percentage === 600
      );

      if (!activeUploads.length) {
        return;
      }

      const filesStillTransferring = activeUploads.some((item: any) => {
        const isFinalError =
          item.uploadFailed === true ||
          item.percentage === 500 ||
          item.percentage === 600 ||
          item.scanStatus === 99;

        const transferCompleted =
          item.uploadStatus === 'complete' ||
          item.scanStatus === 1;

        return item.attachmentKey && !isFinalError && !transferCompleted;
      });

      if (filesStillTransferring) {
        return;
      }

      const allFinal = activeUploads.every((item: any) => {
        return (
          item.uploadStatus === 'failed' ||
          item.scanStatus === 1 ||
          item.scanStatus === 99 ||
          item.uploadFailed === true ||
          item.percentage === 500 ||
          item.percentage === 600
        );
      });

    if (allFinal) {
        clearInterval(this.uploadCompletionTimer);
        this.uploadCompletionTimer = null;
        this.stopStatusPolling();

        const invalidFilePresent = activeUploads.some((item: any) =>
          item.scanStatus === 99 || item.percentage === 500
        );

        const uploadErrorPresent = activeUploads.some((item: any) =>
          item.uploadFailed === true || item.percentage === 600
        );

        if (this.hasErroredFiles) {
          this.cdr.detectChanges();
          return;
        }

        this.uploadCompleted.emit({
          files: this.files,
          hasInvalidFiles: invalidFilePresent,
          hasUploadErrors: uploadErrorPresent
        });
        return;
      }

      const attachmentKeysReadyForReviewAndScan = activeUploads
        .filter((item: any) =>
        item.attachmentKey &&
        item.uploadStatus === 'complete' &&
        item.uploadFailed !== true &&
        item.percentage !== 500 &&
        item.percentage !== 600 &&
        item.scanStatus !== 1 &&
        item.scanStatus !== 99 &&
        !this.reviewAndScanRequestedAttachmentKeys.has(item.attachmentKey)
      )
      .map((item: any) => item.attachmentKey);

      if (attachmentKeysReadyForReviewAndScan.length) {
        this.requestReviewAndScan(attachmentKeysReadyForReviewAndScan);
      }
    }, 1000);
  }

  private markFileUploadError(fileName: string) {
    const uploadFile = this.FilesToUpload.find((x: any) => x.fileName === fileName);
    if (uploadFile) {
      uploadFile.uploadStatus = 'failed';
      uploadFile.statusText = 'Error in upload';
      uploadFile.failureToUpload = true;
      uploadFile.uploadFailed = true;
      uploadFile.isReadyToDownload = false;
      uploadFile.percentage = 600;
    }

    const file = this.files.find((x: any) => x.fileName === fileName);
    if (file) {
      file.uploadStatus = 'failed';
      file.statusText = 'Error in upload';
      file.failureToUpload = true;
      file.uploadFailed = true;
      file.isReadyToDownload = false;
      file.percentage = 600;
      file.isVisible = file.attachmentKey + '_99';
    }

    this.resolvePendingTransfer(fileName);
    this.service.setCacheForitems([{ id: 'files', value: this.files }]);
    this.cdr.detectChanges();
  }

  private markFileInvalid(fileName: string) {
    const uploadFile = this.FilesToUpload.find((x: any) => x.fileName === fileName);
    if (uploadFile) {
      uploadFile.uploadStatus = 'failed';
      uploadFile.statusText = 'Error in upload';
      uploadFile.failureToUpload = true;
      uploadFile.uploadFailed = false;
      uploadFile.scanStatus = 99;
      uploadFile.percentage = 500;
    }

    const file = this.files.find((x: any) => x.fileName === fileName);
    if (file) {
      file.uploadStatus = 'failed';
      file.statusText = 'Error in upload';
      file.failureToUpload = true;
      file.uploadFailed = false;
      file.scanStatus = 99;
      file.percentage = 500;
      file.isVisible = file.attachmentKey + '_99';
    }

    this.resolvePendingTransfer(fileName);
    this.service.setCacheForitems([{ id: 'files', value: this.files }]);
    this.cdr.detectChanges();
  }

  private updateFileSuccess(fileName: string) {
    const uploadFile = this.FilesToUpload.find((x: any) => x.fileName === fileName);
    if (uploadFile) {
      uploadFile.uploadStatus = 'complete';
      uploadFile.statusText = 'Upload complete';
      uploadFile.failureToUpload = false;
      uploadFile.isReadyToDownload = true;
    }

    const file = this.files.find((x: any) => x.fileName === fileName);
    if (file) {
      file.uploadStatus = 'complete';
      file.statusText = 'Upload complete';
      file.failureToUpload = false;
      file.isReadyToDownload = true;
      file.isuploaded = true;
    }

    const attachmentKey = uploadFile?.attachmentKey ?? file?.attachmentKey;
    if (attachmentKey && uploadFile?.isFirst !== true && !this.reviewAndScanRequestedAttachmentKeys.has(attachmentKey)) {
      this.requestReviewAndScan([attachmentKey]);
      if (!this.statusPollingTimer) {
        this.startStatusPolling();
      }
    }

    this.resolvePendingTransfer(fileName);
    this.service.setCacheForitems([{ id: 'files', value: this.files }]);
    this.cdr.detectChanges();
  }

  private waitForTransferCompletion(fileName: string): Promise<void> {
    const file = this.files.find((x: any) => x.fileName === fileName);
    const transferFinished = !!file && (
      file.uploadStatus === 'complete' ||
      file.uploadStatus === 'failed' ||
      file.uploadFailed === true ||
      Number(file.percentage) === 500 ||
      Number(file.percentage) === 600
    );

    if (transferFinished) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.pendingTransferResolvers.set(fileName, resolve);
    });
  }

  private resolvePendingTransfer(fileName: string) {
    const resolver = this.pendingTransferResolvers.get(fileName);
    if (resolver) {
      this.pendingTransferResolvers.delete(fileName);
      resolver();
    }
  }

  private requestReviewAndScan(attachmentKeyList?: any[]) {
    const keysToRequest = (attachmentKeyList || this.files
      .filter((item: any) =>
        item.attachmentKey &&
        item.uploadStatus === 'complete' &&
        item.uploadFailed !== true &&
        item.percentage !== 500 &&
        item.percentage !== 600 &&
        item.scanStatus !== 1 &&
        item.scanStatus !== 99
      )
      .map((item: any) => item.attachmentKey)
    ).filter((attachmentKey: any) =>
      !!attachmentKey && !this.reviewAndScanRequestedAttachmentKeys.has(attachmentKey)
    );

    if (!keysToRequest.length) {
      return;
    }

    keysToRequest.forEach((attachmentKey: any) => this.reviewAndScanRequestedAttachmentKeys.add(attachmentKey));

    this.fcservice.ReviewAndScanAttachmnents({
      referenceId: this.service.getCache('referenceId'),
      attachmentKeyList: keysToRequest
    }).subscribe({
      error: (err: any) => {
        console.error('ReviewAndScanAttachmnents failed', err);
        keysToRequest.forEach((attachmentKey: any) => this.reviewAndScanRequestedAttachmentKeys.delete(attachmentKey));
      }
    });
  }

  getStatusText(file: any): string {
    if (file.scanStatus === 1) {
      return 'Complete';
    }

    if (
      file.uploadStatus === 'failed' ||
      file.uploadFailed === true ||
      file.scanStatus === 99 ||
      Number(file.percentage) === 500 ||
      Number(file.percentage) === 600
    ) {
      return 'Error in Upload';
    }

    return 'In Progress';
  }
  get allFilesComplete(): boolean {
    if (!this.FilesToUpload || this.FilesToUpload.length === 0) {
      return false;
    }
    return this.FilesToUpload.every((f: any) => f.scanStatus === 1);
  }

  get hasErrorInUpload(): boolean {
    if (!this.FilesToUpload || this.FilesToUpload.length === 0) {
      return false;
    }

    const allFilesFinalized = this.FilesToUpload.every((f: any) => {
      const isConfirmedSuccess = f.scanStatus === 1;
      const isConfirmedError = f.scanStatus === 99 || Number(f.percentage) === 500 || Number(f.percentage) === 600 || f.uploadFailed === true;
      return isConfirmedSuccess || isConfirmedError;
    });

    if (!allFilesFinalized) {
      return false;
    }

    return this.FilesToUpload.some((f: any) => {
      return f.scanStatus === 99 || Number(f.percentage) === 500 || Number(f.percentage) === 600 || f.uploadFailed === true;
    });
  }

  removeAndGoBackToUploadPage() {
    const cleanFiles = this.FilesToUpload.filter((f: any) =>
      f.percentage !== 500 &&
      f.percentage !== 600 &&
      f.uploadFailed !== true &&
      f.failureToUpload !== true &&
      f.scanStatus !== 99
    );

    this.FilesToUpload = cleanFiles;
    this.files = cleanFiles;

    this.service.setCacheForitems([
      { id: 'files', value: cleanFiles }
    ]);

    this.uploadCompleted.emit({
      files: cleanFiles,
      hasInvalidFiles: false,
      hasUploadErrors: false
    });

    this.cdr.detectChanges();
  }

  openReUploadModal() {
    this.showReUploadModal = true;
    this.reUploadError = '';
    this.reUploadNewFiles = [];

    const successFiles = this.FilesToUpload.filter((f: any) =>
      f.percentage !== 500 &&
      f.percentage !== 600 &&
      f.uploadFailed !== true &&
      f.failureToUpload !== true &&
      f.scanStatus !== 99
    );

    this.reUploadFilesList = successFiles.map((f: any) => ({
      fileName: f.fileName,
      sizeReadable: f.sizeReadable,
      fileSize: f.fileSize,
      alreadyUploaded: true,
      attachmentKey: f.attachmentKey
    }));

    this.cdr.detectChanges();
  }

  onReUploadFileChange(event: any) {
    const fileList = event.files ? event.files : event;
    if (!fileList || fileList.length === 0) {
      return;
    }

    this.reUploadError = '';
    const incomingFiles = Array.from(fileList) as File[];

    for (let i = 0; i < incomingFiles.length; i++) {
      const file = incomingFiles[i];

      if (this.reUploadFilesList.some((f: any) => f.fileName === file.name)) {
        this.reUploadError += 'Duplicate file detected: ' + file.name + '. ';
        continue;
      }

      const totalFileSize = this.reUploadFilesList.reduce((total: number, f: any) => total + (f.fileSize || 0), 0);
      if ((totalFileSize + file.size) > 1.5 * 1024 * 1024) {
        this.reUploadError += 'The total file size exceeds the limit of 1.5 MB. File Name: ' + file.name + '. ';
        continue;
      }

      this.reUploadNewFiles.push(file);
      this.reUploadFilesList.push({
        fileName: file.name,
        sizeReadable: this.humanFileSize(file.size, true),
        fileSize: file.size,
        alreadyUploaded: false,
        targetFile: file
      });
    }

    if (this.reUploadFileInput?.nativeElement) {
      this.reUploadFileInput.nativeElement.value = '';
    }

    this.cdr.detectChanges();
  }

  removeReUploadFile(file: any) {
    this.reUploadFilesList = this.reUploadFilesList.filter((f: any) => f !== file);
    this.reUploadNewFiles = this.reUploadNewFiles.filter((f: File) => f.name !== file.fileName);
    this.cdr.detectChanges();
  }

  submitReUpload() {
    this.showReUploadModal = false;

    const alreadyUploadedFiles = this.reUploadFilesList.filter((f: any) => f.alreadyUploaded);
    const newFilesToUpload = this.reUploadFilesList.filter((f: any) => !f.alreadyUploaded);

    this.files = this.files.filter((f: any) =>
      f.percentage !== 500 &&
      f.percentage !== 600 &&
      f.uploadFailed !== true &&
      f.failureToUpload !== true &&
      f.scanStatus !== 99
    );

    this.FilesToUpload = this.FilesToUpload.filter((f: any) =>
      f.percentage !== 500 &&
      f.percentage !== 600 &&
      f.uploadFailed !== true &&
      f.failureToUpload !== true &&
      f.scanStatus !== 99
    );

    if (newFilesToUpload.length === 0) {
      this.service.setCacheForitems([{ id: 'files', value: this.files }]);
      this.uploadCompleted.emit({
        files: this.files,
        hasInvalidFiles: false,
        hasUploadErrors: false
      });
      this.cdr.detectChanges();
      return;
    }

    const newFileEntries: any[] = [];
    const maxExistingFileId = this.FilesToUpload.length > 0
      ? Math.max(...this.FilesToUpload.map((f: any) => f.fileId))
      : -1;
    for (let i = 0; i < newFilesToUpload.length; i++) {
      const newFile = newFilesToUpload[i];
      const fileEntry = {
        fileName: newFile.fileName,
        fileType: newFile.fileName.substring(newFile.fileName.lastIndexOf('.') + 1),
        percentage: 0,
        fileSize: newFile.fileSize || 0,
        sizeReadable: newFile.sizeReadable || '',
        isFirst: false,
        fileId: maxExistingFileId + 1 + i,
        activityName: 'Deconfliction',
        referenceId: this.service.getCache('referenceId'),
        targetFile: newFile.targetFile,
        attachmentLength: newFile.fileSize,
        isUpload: 1,
        noOfchunksSent: 0,
        uploadStatus: 'uploading',
        statusText: 'Upload in progress',
        failureToUpload: false
      };
      newFileEntries.push(fileEntry);
      this.files.push(fileEntry);
    }

    if (this.uploadedFiles) {
      this.reUploadNewFiles.forEach((f: File) => {
        this.uploadedFiles!.push(f);
      });
    }

    this.service.setCacheForitems([{ id: 'files', value: this.files }]);
    this.reviewAndScanRequestedAttachmentKeys.clear();
    this.totalUploadedFiles = 0;
    this.fileUploadMap = {};
    this.startStatusPolling();
    this.uploadReUploadFiles(newFileEntries);
    this.waitForUploadCompletion();

    this.cdr.detectChanges();
  }

  private async uploadReUploadFiles(newFileEntries: any[]) {
    const caseActivityKey = this.service.getCache('caseActivityKey');
    for (let i = 0; i < newFileEntries.length; i++) {
      const attachment = { ...newFileEntries[i] };
      if (caseActivityKey) {
        attachment.caseActivityKey = caseActivityKey;
      }
      this.FilesToUpload.push(attachment);
      await this.addAttachmentToDbAsync(attachment, attachment.targetFile, attachment.fileId);
    }
  }

  private restoreAfterReload(): void {

    this.files.forEach((file: any, index: number) => {
      const entry: any = {
        fileName: file.fileName,
        sizeReadable: file.sizeReadable || '',
        fileSize: file.fileSize || 0,
        fileId: index,
        attachmentKey: file.attachmentKey || null,
        uploadStatus: file.uploadStatus || 'uploading',
        statusText: file.statusText || 'Upload in progress',
        scanStatus: file.scanStatus || 0,
        percentage: file.percentage || 0,
        uploadFailed: file.uploadFailed || false,
        failureToUpload: file.failureToUpload || false,
        isReadyToDownload: file.isReadyToDownload || false
      };

      const wasInterrupted =
        !file.attachmentKey ||
        (
          !!file.attachmentKey &&
          Number(file.percentage || 0) < 100 &&
          file.scanStatus !== 1 &&
          file.scanStatus !== 99 &&
          file.uploadFailed !== true
        );

      if (wasInterrupted) {
        entry.uploadStatus = 'failed';
        entry.statusText = 'Error in upload';
        entry.uploadFailed = true;
        entry.failureToUpload = true;
        entry.percentage = 600;

        file.uploadStatus = 'failed';
        file.statusText = 'Error in upload';
        file.uploadFailed = true;
        file.failureToUpload = true;
        file.percentage = 600;
      }

      this.FilesToUpload.push(entry);
    });

    this.service.setCacheForitems([{ id: 'files', value: this.files }]);
    this.cdr.detectChanges();
    this.startStatusPolling();
    this.waitForUploadCompletion();
  }

  private humanFileSize(bytes: number, si: boolean): string {
    const thresh = si ? 1000 : 1024;
    if (Math.abs(bytes) < thresh) {
      return bytes + ' B';
    }
    const units = si
      ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
      : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    do {
      bytes /= thresh;
      ++u;
    } while (Math.abs(bytes) >= thresh && u < units.length - 1);
    return bytes.toFixed(1) + ' ' + units[u];
  }

  get hasErroredFiles(): boolean {
    return this.FilesToUpload?.some((f: any) =>
      f.percentage === 500 ||
      f.percentage === 600 ||
      f.uploadFailed === true ||
      f.failureToUpload === true ||
      f.scanStatus === 99
    );
  }

}
